import React, { useState, useEffect } from 'react';
import { fmtDateTime, fmtDate } from '@shared/formatDate';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Send as SendIcon,
  Download as WithdrawIcon,
  Upload as DepositIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import jointAccountService, {
  JointAccountDetail as JointAccountDetailType,
  JointAccountBalance,
  JointAccountMember,
  JointAccountTransaction,
  InviteMemberRequest,
  DepositRequest,
  SpendRequest,
  WithdrawRequest,
} from '../services/jointAccountService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const JointAccountDetail: React.FC = () => {
  const { jointAccountId } = useParams<{ jointAccountId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [account, setAccount] = useState<JointAccountDetailType | null>(null);
  const [balance, setBalance] = useState<JointAccountBalance | null>(null);
  const [members, setMembers] = useState<JointAccountMember[]>([]);
  const [transactions, setTransactions] = useState<JointAccountTransaction[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<JointAccountTransaction[]>([]);

  // Dialog states
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [spendDialogOpen, setSpendDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<JointAccountMember | null>(null);
  const [editMinorPassword, setEditMinorPassword] = useState('');
  const [editMinorConfirmPassword, setEditMinorConfirmPassword] = useState('');
  const [showEditMinorPassword, setShowEditMinorPassword] = useState(false);

  // Form states
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDescription, setDepositDescription] = useState('');
  const [depositFromPersonal, setDepositFromPersonal] = useState(true);
  const [spendAmount, setSpendAmount] = useState('');
  const [spendDescription, setSpendDescription] = useState('');
  const [spendRecipient, setSpendRecipient] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawDescription, setWithdrawDescription] = useState('');

  // Invite form
  const [inviteForm, setInviteForm] = useState<InviteMemberRequest>({
    username: '',
    role: 'MEMBER',
    displayName: '',
    canSpend: true,
    canDeposit: true,
    canWithdraw: false,
    canInviteMembers: false,
    canEditSettings: false,
    requiresApproval: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Current user's membership
  const [currentMember, setCurrentMember] = useState<JointAccountMember | null>(null);

  useEffect(() => {
    if (jointAccountId) {
      loadAccountData();
    }
  }, [jointAccountId]);

  const loadAccountData = async () => {
    if (!jointAccountId) return;

    try {
      setLoading(true);
      setError(null);

      const [accountData, balanceData, membersData, transactionsData] = await Promise.all([
        jointAccountService.getAccount(jointAccountId),
        jointAccountService.getBalance(jointAccountId),
        jointAccountService.getMembers(jointAccountId),
        jointAccountService.getHistory(jointAccountId),
      ]);

      setAccount(accountData);
      setBalance(balanceData);
      setMembers(membersData);
      setTransactions(transactionsData);

      // Get current user's membership from the members list
      // Note: This assumes the API will include the current user in the members list
      // The backend could also provide this directly
      const userJson = localStorage.getItem('user');
      const userId = userJson ? JSON.parse(userJson).userId : null;
      const userMember = membersData.find(m => m.memberUserId === userId);
      setCurrentMember(userMember || null);

      // Load pending approvals if user can approve
      if (userMember?.role === 'PRIMARY' || userMember?.canEditSettings) {
        const pending = await jointAccountService.getPendingApprovals(jointAccountId);
        setPendingApprovals(pending);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!jointAccountId || !depositAmount) return;

    try {
      setSubmitting(true);
      setSubmitError(null);
      const request: DepositRequest = {
        amount: parseFloat(depositAmount),
        description: depositDescription || undefined,
        fromPersonalAccount: depositFromPersonal,
      };
      const result = await jointAccountService.deposit(jointAccountId, request);
      if (result.success) {
        setDepositDialogOpen(false);
        setDepositAmount('');
        setDepositDescription('');
        loadAccountData();
      } else {
        setSubmitError(result.message || 'Failed to deposit');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to deposit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSpend = async () => {
    if (!jointAccountId || !spendAmount) return;

    try {
      setSubmitting(true);
      setSubmitError(null);
      const request: SpendRequest = {
        amount: parseFloat(spendAmount),
        description: spendDescription || undefined,
        recipient: spendRecipient || undefined,
      };
      const result = await jointAccountService.spend(jointAccountId, request);
      if (result.success) {
        setSpendDialogOpen(false);
        setSpendAmount('');
        setSpendDescription('');
        setSpendRecipient('');
        loadAccountData();
        if (result.pendingApproval) {
          alert('Transaction submitted for approval');
        }
      } else {
        setSubmitError(result.message || 'Failed to process spending');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to process spending');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!jointAccountId || !withdrawAmount) return;

    try {
      setSubmitting(true);
      setSubmitError(null);
      const request: WithdrawRequest = {
        amount: parseFloat(withdrawAmount),
        description: withdrawDescription || undefined,
      };
      const result = await jointAccountService.withdraw(jointAccountId, request);
      if (result.success) {
        setWithdrawDialogOpen(false);
        setWithdrawAmount('');
        setWithdrawDescription('');
        loadAccountData();
      } else {
        setSubmitError(result.message || 'Failed to withdraw');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to withdraw');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInviteMember = async () => {
    if (!jointAccountId || !inviteForm.username) return;

    try {
      setSubmitting(true);
      setSubmitError(null);
      const result = await jointAccountService.inviteMember(jointAccountId, inviteForm);
      if (result.success) {
        setInviteDialogOpen(false);
        setInviteForm({
          username: '',
          role: 'MEMBER',
          displayName: '',
          canSpend: true,
          canDeposit: true,
          canWithdraw: false,
          canInviteMembers: false,
          canEditSettings: false,
          requiresApproval: false,
        });
        loadAccountData();
      } else {
        setSubmitError(result.message || 'Failed to invite member');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to invite member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveTransaction = async (transactionId: string, approve: boolean) => {
    if (!jointAccountId) return;

    try {
      await jointAccountService.approveTransaction(jointAccountId, transactionId, approve);
      loadAccountData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to process approval');
    }
  };

  const handleRemoveMember = async (membershipId: string) => {
    if (!jointAccountId) return;
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      await jointAccountService.removeMember(jointAccountId, membershipId);
      loadAccountData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleOpenEditMember = (member: JointAccountMember) => {
    setEditingMember(member);
    setEditMinorPassword('');
    setEditMinorConfirmPassword('');
    setShowEditMinorPassword(false);
    setEditMemberDialogOpen(true);
  };

  const handleUpdateMember = async () => {
    if (!jointAccountId || !editingMember) return;

    if (editingMember.role === 'MINOR' && editMinorPassword) {
      if (editMinorPassword.length < 8) {
        setSubmitError('Password must be at least 8 characters');
        return;
      }
      if (editMinorPassword !== editMinorConfirmPassword) {
        setSubmitError('Passwords do not match');
        return;
      }
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      
      const result = await jointAccountService.updateMember(
        jointAccountId, 
        editingMember.membershipId, 
        {
          displayName: editingMember.displayName,
          role: editingMember.role,
          canSpend: editingMember.canSpend,
          canDeposit: editingMember.canDeposit,
          canWithdraw: editingMember.canWithdraw,
          canInviteMembers: editingMember.canInviteMembers,
          canEditSettings: editingMember.canEditSettings,
          dailyLimit: editingMember.dailyLimit,
          weeklyLimit: editingMember.weeklyLimit,
          monthlyLimit: editingMember.monthlyLimit,
          perTransactionLimit: editingMember.perTransactionLimit,
          requiresApproval: editingMember.requiresApproval,
          newPassword: editingMember.role === 'MINOR' && editMinorPassword ? editMinorPassword : undefined,
        }
      );
      
      if (result.success) {
        setEditMemberDialogOpen(false);
        setEditingMember(null);
        setEditMinorPassword('');
        setEditMinorConfirmPassword('');
        setShowEditMinorPassword(false);
        loadAccountData();
      } else {
        setSubmitError(result.message || 'Failed to update member');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to update member');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Calculate available credits for a member based on their spending limits
  const calculateAvailableCredits = (member: JointAccountMember): { available: number; limitType: string } | null => {
    const limits: { remaining: number; type: string }[] = [];

    if (member.dailyLimit !== undefined && member.dailyLimit > 0) {
      limits.push({ 
        remaining: Math.max(0, member.dailyLimit - (member.currentDailySpent || 0)), 
        type: 'Daily' 
      });
    }
    if (member.weeklyLimit !== undefined && member.weeklyLimit > 0) {
      limits.push({ 
        remaining: Math.max(0, member.weeklyLimit - (member.currentWeeklySpent || 0)), 
        type: 'Weekly' 
      });
    }
    if (member.monthlyLimit !== undefined && member.monthlyLimit > 0) {
      limits.push({ 
        remaining: Math.max(0, member.monthlyLimit - (member.currentMonthlySpent || 0)), 
        type: 'Monthly' 
      });
    }

    if (limits.length === 0) return null;

    // Return the most restrictive limit (smallest remaining amount)
    const mostRestrictive = limits.reduce((min, curr) => 
      curr.remaining < min.remaining ? curr : min
    );

    return { available: mostRestrictive.remaining, limitType: mostRestrictive.type };
  };

  const formatDate = (dateStr: string) => fmtDateTime(dateStr);

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'DEPOSIT':
        return 'success';
      case 'SPEND':
        return 'error';
      case 'WITHDRAW':
        return 'warning';
      case 'ALLOWANCE':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!account) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">{error || 'Account not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/joint-accounts')} sx={{ mt: 2 }}>
          Back to Joint Accounts
        </Button>
      </Container>
    );
  }

  const canDeposit = currentMember?.canDeposit ?? false;
  const canSpend = currentMember?.canSpend ?? false;
  const canWithdraw = currentMember?.canWithdraw ?? false;
  const canInvite = currentMember?.canInviteMembers || currentMember?.role === 'PRIMARY';
  const canApprove = currentMember?.role === 'PRIMARY' || currentMember?.canEditSettings;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/joint-accounts')} sx={{ mb: 2 }}>
        Back to Joint Accounts
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4">{account.accountName}</Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip
                label={account.accountType}
                color={account.accountType === 'FAMILY' ? 'warning' : 'info'}
              />
              <Chip
                label={currentMember?.role || 'MEMBER'}
                variant="outlined"
              />
            </Box>
            {account.description && (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {account.description}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Box textAlign={{ xs: 'left', md: 'right' }}>
              <Typography variant="h3" color="primary">
                {formatCurrency(balance?.availableBalance || 0)}
              </Typography>
              <Typography color="text.secondary">Available Balance</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Action buttons */}
        <Divider sx={{ my: 2 }} />
        <Box display="flex" gap={2} flexWrap="wrap">
          {/* For family accounts, only show Add Minor button */}
          {account.isFamilyAccount ? (
            <>
              {canInvite && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setInviteForm({
                      username: '',
                      role: 'MINOR',
                      displayName: '',
                      canSpend: true,
                      canDeposit: false,
                      canWithdraw: false,
                      canInviteMembers: false,
                      canEditSettings: false,
                      requiresApproval: true,
                    });
                    setInviteDialogOpen(true);
                  }}
                >
                  Add Minor
                </Button>
              )}
            </>
          ) : (
            <>
              {canDeposit && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DepositIcon />}
                  onClick={() => setDepositDialogOpen(true)}
                >
                  Deposit
                </Button>
              )}
              {canSpend && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={() => setSpendDialogOpen(true)}
                >
                  Spend
                </Button>
              )}
              {canWithdraw && (
                <Button
                  variant="outlined"
                  startIcon={<WithdrawIcon />}
                  onClick={() => setWithdrawDialogOpen(true)}
                >
                  Withdraw
                </Button>
              )}
              {canInvite && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setInviteForm({
                      username: '',
                      role: 'MEMBER',
                      displayName: '',
                      canSpend: true,
                      canDeposit: true,
                      canWithdraw: false,
                      canInviteMembers: false,
                      canEditSettings: false,
                      requiresApproval: false,
                    });
                    setInviteDialogOpen(true);
                  }}
                >
                  Invite Member
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Spending limits display */}
        {balance && (balance.dailyLimit || balance.weeklyLimit || balance.monthlyLimit) && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Your Spending Limits
            </Typography>
            <Grid container spacing={2}>
              {balance.dailyLimit && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Daily: {formatCurrency(balance.dailySpent || 0)} / {formatCurrency(balance.dailyLimit)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((balance.dailySpent || 0) / balance.dailyLimit) * 100)}
                    color={(balance.dailySpent || 0) >= balance.dailyLimit ? 'error' : 'primary'}
                  />
                </Grid>
              )}
              {balance.weeklyLimit && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Weekly: {formatCurrency(balance.weeklySpent || 0)} / {formatCurrency(balance.weeklyLimit)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((balance.weeklySpent || 0) / balance.weeklyLimit) * 100)}
                    color={(balance.weeklySpent || 0) >= balance.weeklyLimit ? 'error' : 'primary'}
                  />
                </Grid>
              )}
              {balance.monthlyLimit && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Monthly: {formatCurrency(balance.monthlySpent || 0)} / {formatCurrency(balance.monthlyLimit)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((balance.monthlySpent || 0) / balance.monthlyLimit) * 100)}
                    color={(balance.monthlySpent || 0) >= balance.monthlyLimit ? 'error' : 'primary'}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Pending Approvals Alert */}
      {canApprove && pendingApprovals.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {pendingApprovals.length} transaction{pendingApprovals.length > 1 ? 's' : ''} pending approval
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Members (${members.length})`} />
          <Tab label="History" />
          {canApprove && pendingApprovals.length > 0 && (
            <Tab label={`Pending (${pendingApprovals.length})`} />
          )}
        </Tabs>

        {/* Members Tab (first/default) */}
        <TabPanel value={tab} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>Limits</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell>Joined</TableCell>
                  {canInvite && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.membershipId}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body2">
                            {member.displayName || member.username}
                          </Typography>
                          {member.displayName && (
                            <Typography variant="caption" color="text.secondary">
                              @{member.invitedAs || member.username}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={member.role} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {member.canSpend && <Chip label="Spend" size="small" variant="outlined" />}
                        {member.canDeposit && <Chip label="Deposit" size="small" variant="outlined" />}
                        {member.canWithdraw && <Chip label="Withdraw" size="small" variant="outlined" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {member.dailyLimit ? `Daily: ${formatCurrency(member.dailyLimit)}` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const credits = calculateAvailableCredits(member);
                        if (!credits) return '-';
                        return (
                          <Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: credits.available > 0 ? 'success.main' : 'error.main'
                              }}
                            >
                              {formatCurrency(credits.available)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({credits.limitType})
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{fmtDate(member.joinedDate)}</TableCell>
                    {canInvite && (
                      <TableCell align="right">
                        {member.role !== 'PRIMARY' && (
                          <>
                            <Tooltip title="Edit Member">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditMember(member)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove Member">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(member.membershipId)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* History Tab */}
        <TabPanel value={tab} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Member</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.transactionId}>
                    <TableCell>{formatDate(tx.created)}</TableCell>
                    <TableCell>
                      <Chip
                        label={tx.transactionType}
                        size="small"
                        color={getTransactionTypeColor(tx.transactionType) as any}
                      />
                    </TableCell>
                    <TableCell>{tx.memberDisplayName || tx.memberUsername}</TableCell>
                    <TableCell>{tx.description || tx.recipient || '-'}</TableCell>
                    <TableCell align="right" sx={{ 
                      color: tx.transactionType === 'DEPOSIT' || tx.transactionType === 'ALLOWANCE' 
                        ? 'success.main' 
                        : 'error.main' 
                    }}>
                      {tx.transactionType === 'DEPOSIT' || tx.transactionType === 'ALLOWANCE' ? '+' : '-'}
                      {formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell align="right">
                      {tx.balanceAfter !== undefined ? formatCurrency(tx.balanceAfter) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {transactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No transactions yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Pending Approvals Tab */}
        {canApprove && pendingApprovals.length > 0 && (
          <TabPanel value={tab} index={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Member</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingApprovals.map((tx) => (
                    <TableRow key={tx.transactionId}>
                      <TableCell>{formatDate(tx.created)}</TableCell>
                      <TableCell>{tx.memberDisplayName || tx.memberUsername}</TableCell>
                      <TableCell>
                        <Chip label={tx.transactionType} size="small" />
                      </TableCell>
                      <TableCell>{tx.description || tx.recipient || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(tx.amount)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Approve">
                          <IconButton
                            color="success"
                            onClick={() => handleApproveTransaction(tx.transactionId, true)}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            color="error"
                            onClick={() => handleApproveTransaction(tx.transactionId, false)}
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        )}

      </Paper>

      {/* Deposit Dialog */}
      <Dialog open={depositDialogOpen} onClose={() => setDepositDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deposit Funds</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            InputProps={{ startAdornment: '$' }}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={depositDescription}
            onChange={(e) => setDepositDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={depositFromPersonal}
                onChange={(e) => setDepositFromPersonal(e.target.checked)}
              />
            }
            label="Transfer from personal account"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDepositDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleDeposit} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Deposit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spend Dialog */}
      <Dialog open={spendDialogOpen} onClose={() => setSpendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Spend from Account</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={spendAmount}
            onChange={(e) => setSpendAmount(e.target.value)}
            InputProps={{ startAdornment: '$' }}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Recipient (optional)"
            value={spendRecipient}
            onChange={(e) => setSpendRecipient(e.target.value)}
            placeholder="e.g., Store name, vendor"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={spendDescription}
            onChange={(e) => setSpendDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          {currentMember?.requiresApproval && (
            <Alert severity="info">
              This transaction will require approval from an account administrator.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpendDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSpend} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Spend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Withdraw to Personal Account</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            InputProps={{ startAdornment: '$' }}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={withdrawDescription}
            onChange={(e) => setWithdrawDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleWithdraw} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={editMemberDialogOpen} onClose={() => setEditMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member Settings</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          
          {editingMember && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Editing: @{editingMember.username}
              </Typography>
              
              <TextField
                fullWidth
                label="Display Name"
                value={editingMember.displayName || ''}
                onChange={(e) => setEditingMember({ ...editingMember, displayName: e.target.value })}
                placeholder="e.g., Child's name"
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editingMember.role}
                  label="Role"
                  onChange={(e) => setEditingMember({
                    ...editingMember,
                    role: e.target.value as 'PRIMARY' | 'ADMIN' | 'MEMBER' | 'MINOR',
                  })}
                  disabled={editingMember.role === 'PRIMARY'}
                >
                  <MenuItem value="ADMIN">Admin</MenuItem>
                  <MenuItem value="MEMBER">Member</MenuItem>
                  <MenuItem value="MINOR">Minor</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={editingMember.canSpend} 
                        onChange={(e) => setEditingMember({ ...editingMember, canSpend: e.target.checked })} 
                      />
                    }
                    label="Can Spend"
                  />
                </Grid>
                {/* Only show these options for business accounts, not family accounts */}
                {!account.isFamilyAccount && (
                  <>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={editingMember.canDeposit} 
                            onChange={(e) => setEditingMember({ ...editingMember, canDeposit: e.target.checked })} 
                          />
                        }
                        label="Can Deposit"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={editingMember.canWithdraw} 
                            onChange={(e) => setEditingMember({ ...editingMember, canWithdraw: e.target.checked })} 
                          />
                        }
                        label="Can Withdraw"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={editingMember.requiresApproval} 
                        onChange={(e) => setEditingMember({ ...editingMember, requiresApproval: e.target.checked })} 
                      />
                    }
                    label={account.isFamilyAccount ? "Approve All Purchases" : "Requires Approval"}
                  />
                </Grid>
                {/* Only show these options for business accounts */}
                {!account.isFamilyAccount && (
                  <>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={editingMember.canInviteMembers} 
                            onChange={(e) => setEditingMember({ ...editingMember, canInviteMembers: e.target.checked })} 
                          />
                        }
                        label="Can Invite"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={editingMember.canEditSettings} 
                            onChange={(e) => setEditingMember({ ...editingMember, canEditSettings: e.target.checked })} 
                          />
                        }
                        label="Can Edit Settings"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              {/* Explanation for family accounts */}
              {account.isFamilyAccount && editingMember.requiresApproval && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  When enabled, every purchase by this minor will require your approval before it goes through.
                </Alert>
              )}

              {editingMember.role === 'MINOR' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LockIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2">Change Password</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Leave blank to keep the current password. You can reset a minor&apos;s password at any time.
                  </Typography>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showEditMinorPassword ? 'text' : 'password'}
                    value={editMinorPassword}
                    onChange={(e) => setEditMinorPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="At least 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowEditMinorPassword(v => !v)}>
                            {showEditMinorPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showEditMinorPassword ? 'text' : 'password'}
                    value={editMinorConfirmPassword}
                    onChange={(e) => setEditMinorConfirmPassword(e.target.value)}
                    error={!!editMinorConfirmPassword && editMinorPassword !== editMinorConfirmPassword}
                    helperText={
                      editMinorConfirmPassword && editMinorPassword !== editMinorConfirmPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {/* Current Available Credits (read-only display) */}
              {(() => {
                const credits = calculateAvailableCredits(editingMember);
                if (!credits) return null;
                return (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      bgcolor: credits.available > 0 ? 'success.50' : 'error.50',
                      borderColor: credits.available > 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>Current Available Credits</Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: credits.available > 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {formatCurrency(credits.available)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Based on {credits.limitType.toLowerCase()} limit
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        {editingMember.dailyLimit !== undefined && editingMember.dailyLimit > 0 && (
                          <Typography variant="caption" display="block">
                            Daily: {formatCurrency(editingMember.currentDailySpent || 0)} / {formatCurrency(editingMember.dailyLimit)}
                          </Typography>
                        )}
                        {editingMember.weeklyLimit !== undefined && editingMember.weeklyLimit > 0 && (
                          <Typography variant="caption" display="block">
                            Weekly: {formatCurrency(editingMember.currentWeeklySpent || 0)} / {formatCurrency(editingMember.weeklyLimit)}
                          </Typography>
                        )}
                        {editingMember.monthlyLimit !== undefined && editingMember.monthlyLimit > 0 && (
                          <Typography variant="caption" display="block">
                            Monthly: {formatCurrency(editingMember.currentMonthlySpent || 0)} / {formatCurrency(editingMember.monthlyLimit)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })()}

              <Typography variant="subtitle2" gutterBottom>Spending Limits</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Daily Limit"
                    type="number"
                    value={editingMember.dailyLimit || ''}
                    onChange={(e) => setEditingMember({ 
                      ...editingMember, 
                      dailyLimit: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    InputProps={{ startAdornment: '$' }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Weekly Limit"
                    type="number"
                    value={editingMember.weeklyLimit || ''}
                    onChange={(e) => setEditingMember({ 
                      ...editingMember, 
                      weeklyLimit: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    InputProps={{ startAdornment: '$' }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Monthly Limit"
                    type="number"
                    value={editingMember.monthlyLimit || ''}
                    onChange={(e) => setEditingMember({ 
                      ...editingMember, 
                      monthlyLimit: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    InputProps={{ startAdornment: '$' }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Per Transaction"
                    type="number"
                    value={editingMember.perTransactionLimit || ''}
                    onChange={(e) => setEditingMember({ 
                      ...editingMember, 
                      perTransactionLimit: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    InputProps={{ startAdornment: '$' }}
                    size="small"
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMemberDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateMember} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{account.isFamilyAccount ? 'Add Minor to Family Account' : 'Invite Member to Business Account'}</DialogTitle>
        <DialogContent>
          {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          <TextField
            fullWidth
            label="Username"
            value={inviteForm.username}
            onChange={(e) => setInviteForm({ ...inviteForm, username: e.target.value })}
            placeholder="Enter username or alias"
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Display Name (optional)"
            value={inviteForm.displayName}
            onChange={(e) => setInviteForm({ ...inviteForm, displayName: e.target.value })}
            placeholder="e.g., Child's name"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteForm.role}
              label="Role"
              onChange={(e) => setInviteForm({
                ...inviteForm,
                role: e.target.value as 'ADMIN' | 'MEMBER' | 'MINOR',
                requiresApproval: e.target.value === 'MINOR' ? true : inviteForm.requiresApproval,
              })}
            >
              {/* Family accounts can only add minors */}
              {account.isFamilyAccount ? (
                <MenuItem value="MINOR">Minor (with parental controls)</MenuItem>
              ) : (
                <>
                  <MenuItem value="ADMIN">Admin (can manage members)</MenuItem>
                  <MenuItem value="MEMBER">Member (standard permissions)</MenuItem>
                </>
              )}
            </Select>
          </FormControl>

          {/* Guidance text for account type */}
          {account.isFamilyAccount && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Family accounts are for adding minors with limited spending access. Enter a username for the minor - this will create a new alias they can use to log in.
            </Alert>
          )}
          {!account.isFamilyAccount && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Enter an existing username to invite them to this business account. They will receive a notification and be assigned a new username for this account.
            </Alert>
          )}

          <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={inviteForm.canSpend} onChange={(e) => setInviteForm({ ...inviteForm, canSpend: e.target.checked })} />}
                label="Can Spend"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={inviteForm.canDeposit} onChange={(e) => setInviteForm({ ...inviteForm, canDeposit: e.target.checked })} />}
                label="Can Deposit"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={inviteForm.canWithdraw} onChange={(e) => setInviteForm({ ...inviteForm, canWithdraw: e.target.checked })} />}
                label="Can Withdraw"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={inviteForm.requiresApproval} onChange={(e) => setInviteForm({ ...inviteForm, requiresApproval: e.target.checked })} />}
                label="Requires Approval"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Spending Limits (optional)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Daily Limit"
                type="number"
                value={inviteForm.dailyLimit || ''}
                onChange={(e) => setInviteForm({ ...inviteForm, dailyLimit: e.target.value ? parseFloat(e.target.value) : undefined })}
                InputProps={{ startAdornment: '$' }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Per Transaction"
                type="number"
                value={inviteForm.perTransactionLimit || ''}
                onChange={(e) => setInviteForm({ ...inviteForm, perTransactionLimit: e.target.value ? parseFloat(e.target.value) : undefined })}
                InputProps={{ startAdornment: '$' }}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleInviteMember} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : (account.isFamilyAccount ? 'Add Minor' : 'Invite Member')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JointAccountDetail;
